import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { ErrorNote } from '../App.js';
import { api } from '../api.js';
import { formatDate, formatDay, useI18n } from '../i18n.js';
import { Icon } from '../ui.js';

export function Keys() {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const keys = useQuery({ queryKey: ['keys'], queryFn: () => api.keys() });
  const [form, setForm] = useState({ name: '', scope: 'write' as 'read' | 'write', days: '365' });
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api.createKey({
        name: form.name,
        scope: form.scope,
        expiresInDays: form.days ? Number(form.days) : null,
      }),
    onSuccess: (k) => {
      setCreated(k.key);
      setForm({ ...form, name: '' });
      void qc.invalidateQueries({ queryKey: ['keys'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteKey(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['keys'] }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <div className="page">
      <h1 className="page-title">{t('apiKeys')}</h1>
      {created && <CreatedKey value={created} />}
      <form className="panel form-panel" onSubmit={submit}>
        <div className="form-grid">
          <label className="span-2">
            {t('name')}
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label>
            {t('scope')}
            <select
              value={form.scope}
              onChange={(e) => setForm({ ...form, scope: e.target.value as 'read' | 'write' })}
            >
              <option value="write">{t('write')}</option>
              <option value="read">{t('read')}</option>
            </select>
          </label>
          <label>
            {t('validFor')}
            <select value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })}>
              <option value="30">30 {t('days')}</option>
              <option value="90">90 {t('days')}</option>
              <option value="365">365 {t('days')}</option>
              <option value="">{t('never')}</option>
            </select>
          </label>
        </div>
        <div>
          <button className="primary" disabled={create.isPending}>
            {t('newKey')}
          </button>
        </div>
      </form>
      <ErrorNote error={keys.error ?? create.error ?? remove.error} />
      {(keys.data?.length ?? 0) > 0 && (
        <div className="sheet section">
          <table className="stack-sm">
            <thead>
              <tr>
                <th>{t('name')}</th>
                <th className="hide-sm">{t('scope')}</th>
                <th className="hide-sm">{t('expires')}</th>
                <th className="hide-sm">{t('lastUsed')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(keys.data ?? []).map((k) => (
                <tr key={k.id}>
                  <td className="cell-grow">
                    <div className="cell-strong">{k.name}</div>
                    <div className="muted">
                      {k.start}…{' '}
                      <span className="show-sm">{k.scope === 'read' ? t('read') : t('write')}</span>
                    </div>
                  </td>
                  <td className="hide-sm">{k.scope === 'read' ? t('read') : t('write')}</td>
                  <td className="hide-sm nowrap">
                    {k.expiresAt ? formatDay(k.expiresAt, lang) : t('never')}
                  </td>
                  <td className="hide-sm nowrap">{k.lastUsedAt ? formatDate(k.lastUsedAt, lang) : '—'}</td>
                  <td className="actions">
                    <button className="quiet danger" onClick={() => remove.mutate(k.id)}>
                      {t('delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** A new key, shown once, with a copy button. */
function CreatedKey({ value }: { value: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <div className="panel created-key" role="status">
      <p>{t('copyKey')}</p>
      <div className="key-row">
        <code className="mono break">{value}</code>
        <button
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
            } catch {
              /* clipboard unavailable: the key stays selectable */
            }
          }}
        >
          <Icon.Copy width={16} height={16} />
          {copied ? t('copied') : t('copy')}
        </button>
      </div>
    </div>
  );
}
